<template>
  <div class="wang-editor-wrapper" style="border: 1px solid #ccc;">
    <Toolbar
      style="border-bottom: 1px solid #ccc"
      :editor="editor"
      :defaultConfig="toolbarConfig"
      :mode="mode"
    />
    <Editor
      :style="{ height: height + 'px', overflowY: 'hidden' }"
      v-model="html"
      :defaultConfig="editorConfig"
      :mode="mode"
      @onCreated="onCreated"
      @onChange="onChange"
    />
  </div>
</template>

<script>
import '@wangeditor/editor/dist/css/style.css'
import Vue from 'vue'
import { Editor, Toolbar } from '@wangeditor/editor-for-vue'
import { uploadFileInfo } from '@/api/upload/upload'

export default Vue.extend({
  name: 'WangEditor',
  components: {
    Editor,
    Toolbar
  },
  props: {
    value: {
      type: String,
      default: ''
    },
    height: {
      type: Number,
      default: 400
    },
    placeholder: {
      type: String,
      default: '请输入内容...'
    },
    mode: {
      type: String,
      default: 'default' // 'default' 或 'simple'
    }
  },
  data() {
    // 获取 imageUrlprefix 的辅助函数
    const getImageUrlPrefix = () => {
      return Vue.prototype.imageUrlprefix || process.env.VUE_APP_IMAGEURLPREFIX || ''
    }
    
    return {
      editor: null,
      html: this.value || '',
      toolbarConfig: {
        // 工具栏配置，可以根据需要自定义
      },
      editorConfig: {
        placeholder: this.placeholder,
        MENU_CONF: {
          // 配置图片上传
          uploadImage: {
            // 自定义上传
            async customUpload(file, insertFn) {
              const imageUrlprefix = getImageUrlPrefix()
              
              try {
                // 使用 uploadFileInfo 上传
                const res = await uploadFileInfo(file, 'baojiuye_policy_file')
                
                if (res && res.code === 200) {
                  // 获取图片URL，根据实际返回的数据结构调整
                  let imageUrl = res.data?.fileUrl || res.data?.url || res.data?.path || res.data
                  
                  if (imageUrl) {
                    // 如果 URL 不是完整的（不以 http:// 或 https:// 开头），则拼接前缀
                    if (imageUrl && !imageUrl.startsWith('http://') && !imageUrl.startsWith('https://') && !imageUrl.startsWith('//')) {
                      imageUrl = imageUrlprefix + imageUrl
                    }
                    
                    // 插入图片到编辑器
                    insertFn(imageUrl, file.name || '图片', imageUrl)
                  } else {
                    throw new Error('上传成功但未返回图片URL')
                  }
                } else {
                  throw new Error(res?.msg || '上传失败')
                }
              } catch (error) {
                console.error('图片上传失败:', error)
                Vue.prototype.$message?.error(error.message || '图片上传失败')
              }
            }
          },
          // 配置视频上传（如果需要）
          uploadVideo: {
            // 自定义上传
            async customUpload(file, insertFn) {
              const imageUrlprefix = getImageUrlPrefix()
              
              try {
                // 使用 uploadFileInfo 上传
                const res = await uploadFileInfo(file, 'baojiuye_policy_file')
                
                if (res && res.code === 200) {
                  // 获取视频URL，根据实际返回的数据结构调整
                  let videoUrl = res.data?.fileUrl || res.data?.url || res.data?.path || res.data
                  
                  if (videoUrl) {
                    // 如果 URL 不是完整的（不以 http:// 或 https:// 开头），则拼接前缀
                    if (videoUrl && !videoUrl.startsWith('http://') && !videoUrl.startsWith('https://') && !videoUrl.startsWith('//')) {
                      videoUrl = imageUrlprefix + videoUrl
                    }
                    
                    // 插入视频到编辑器
                    insertFn(videoUrl, file.name || '视频')
                  } else {
                    throw new Error('上传成功但未返回视频URL')
                  }
                } else {
                  throw new Error(res?.msg || '上传失败')
                }
              } catch (error) {
                console.error('视频上传失败:', error)
                Vue.prototype.$message?.error(error.message || '视频上传失败')
              }
            }
          }
        }
      }
    }
  },
  watch: {
    value: {
      handler(newVal) {
        if (newVal !== this.html && this.editor) {
          try {
            this.html = newVal || ''
            // 只有内容真正不同时才设置，避免触发 onChange
            const currentHtml = this.editor.getHtml()
            if (currentHtml !== this.html) {
              this.editor.setHtml(this.html)
            }
          } catch (error) {
            console.warn('Editor setHtml error:', error)
          }
        }
      },
      immediate: true
    }
  },
  beforeDestroy() {
    this._isDestroying = true
    if (this._changeTimer) {
      clearTimeout(this._changeTimer)
    }
    if (this.editor) {
      try {
        const editorInstance = this.editor
        this.editor = null
        this.$nextTick(() => {
          if (editorInstance && typeof editorInstance.destroy === 'function') {
            try {
              editorInstance.destroy()
            } catch (error) {
              // 忽略销毁时的错误
            }
          }
        })
      } catch (error) {
        // 忽略销毁时的错误
      }
    }
  },
  methods: {
    onCreated(editor) {
      // 使用 Object.seal 防止编辑器实例被 Vue 响应式化
      this.editor = Object.seal(editor)
      // 设置初始内容
      if (this.value) {
        try {
          this.editor.setHtml(this.value)
        } catch (error) {
          console.warn('Editor initial setHtml error:', error)
        }
      }
    },
    onChange(editor) {
      try {
        // 使用防抖避免频繁触发
        if (this._changeTimer) {
          clearTimeout(this._changeTimer)
        }
        this._changeTimer = setTimeout(() => {
          if (this.editor && !this._isDestroying) {
            try {
              const html = editor.getHtml()
              this.html = html
              this.$emit('input', html)
              this.$emit('change', html)
            } catch (error) {
              // 如果编辑器已被销毁，忽略错误
              if (!error.message || !error.message.includes('revoked')) {
                console.warn('Editor onChange error:', error)
              }
            }
          }
        }, 100)
      } catch (error) {
        // 忽略已被撤销的代理错误
        if (!error.message || !error.message.includes('revoked')) {
          console.warn('Editor onChange handler error:', error)
        }
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.wang-editor-wrapper {
  border: 1px solid #ccc;
  border-radius: 4px;
  overflow: hidden;
}
</style>

